import { type NextRouter, useRouter } from 'next/router'
import React from 'react'

import { windowLocation } from '@front/logic/others/windowLocation'
import {
  type InstitutionTabs,
  type MandatoryDocumentType,
  type OfferTabs,
  type OptionalDocumentType,
  type WorkerIsNotEligibleForMissionError,
} from '@front/type/worker'
import type { MyDocumentsTabs } from '@front/type/worker/my-documents-tabs.enum'
import type { ShiftApplicationState } from '@hublo/enums'
import { omit } from 'remeda'
import type { InstitutionDetailsTabs } from '@front/type/worker/institution-details-tabs.enum'

type SnackBarStatus = 'success' | 'warning' | 'error'

interface SnackbarQuery {
  status: SnackBarStatus
  color?: string
  message: string
}

interface ConfettiQuery {
  applySuccessConfetti?: boolean
}

interface JobQuery {
  jobId: number
}

interface PoolQuery {
  fromPoolProfile: boolean
}

interface InstitutionGroupQuery {
  networkId: string
}

interface RedirectQuery {
  redirect: string
}

interface RedirectUrlQuery {
  redirectUrl: string
}

interface InstitutionQuery {
  institutionId: number
}

interface OnboardingStatusQuery {
  groupIndex: number
}

interface DocumentQuery {
  documentToDisplay: number
}

export type ScrollPositionQuery = {
  scroll: number
  itemIndex?: number
  offerTab?: OfferTabs
}

export type FromAddAvailabilitiesForm = {
  scrollToDate: string
}
export type ShiftDealingQuery = {
  comment: string
}
export type ShiftApplyQuery = {
  isDeclined: boolean
  scroll?: number
  itemIndex?: number
  offerTab?: OfferTabs
}

export type OfferFilterQuery = {
  selectedFilter: string
}

export type MultipleShiftsQuery = {
  shiftIds: string[]
}

export type WithPreviousPageQuery = {
  previousPage: string
}

export type CancelShiftQuery = {
  adminEmail: string
  adminName: string
  adminPhoneNumber: string
  servicePhoneNumber: string
  institutionPhoneNumber: string
  shiftApplicationState: ShiftApplicationState
  shiftContext: string
  shiftDate: string
}

export type ContactShiftQuery = {
  adminEmail: string
  adminName: string
  adminPhoneNumber: string
  shiftContext: string
}

export type ResetPasswordQuery = { email: string } | { phone: string }

export type MyInstitutionsTabQuery = {
  institutionTab: InstitutionTabs
}

export type MyDocumentsTabQuery = {
  myDocumentsTab?: MyDocumentsTabs
  hasContractsToBeSigned?: boolean
}

export type OffersTabQuery = {
  offerTab: OfferTabs
  scroll?: number
  itemIndex?: number
}

export type ScheduleTabQuery = {
  selectedDay?: string
  selectedMonth?: string
  applySuccessConfetti?: boolean
}

export type ShiftDetailsV2TabQuery = {
  id: string
  tab: 'details' | 'institution'
  offerTab?: OfferTabs
}

export type DocumentDetailsPageQuery = {
  type: MandatoryDocumentType | OptionalDocumentType
}

export type MstaffJobOffersQuery = {
  url: string
}

export type ProfileAvailabilitiesQuery = {
  day: string
}

export type CancellationTrackingQuery = {
  filter: 'noShowShifts' | 'lateCancelationShifts'
}

export type ShiftHistoryQuery = {
  year: string
  shiftId: string
}

type FromSearchInstitutionQuery = {
  fromSearch: boolean
}

type OneSignalQuery = {
  oneSignal: string
}

type TermOfUseQuery = {
  acceptTermsOfUse: boolean
}

type PrivacyPolicyQuery = {
  acceptPrivacyPolicy: boolean
}

type NotificationByTemplateQuery = {
  type: 'poolOfferCreation' | 'detailed' | 'summary'
}

export type InstitutionDetailsTabQuery = {
  institutionDetailsTab: InstitutionDetailsTabs
  scroll?: number
  itemIndex?: number
}

export type NavQuery =
  | JobQuery
  | InstitutionGroupQuery
  | InstitutionQuery
  | OnboardingStatusQuery
  | RedirectQuery
  | RedirectUrlQuery
  | OneSignalQuery
  | ScrollPositionQuery
  | FromAddAvailabilitiesForm
  | ResetPasswordQuery
  | DocumentQuery
  | MissionApplicationFailedQuery
  | ShiftDealingQuery
  | ShiftApplyQuery
  | OfferFilterQuery
  | MultipleShiftsQuery
  | WithPreviousPageQuery
  | CancelShiftQuery
  | ContactShiftQuery
  | MyInstitutionsTabQuery
  | ShiftDetailsV2TabQuery
  | DocumentDetailsPageQuery
  | OffersTabQuery
  | MstaffJobOffersQuery
  | ScheduleTabQuery
  | ProfileAvailabilitiesQuery
  | CancellationTrackingQuery
  | PoolQuery
  | ShiftHistoryQuery
  | FromSearchInstitutionQuery
  | MyDocumentsTabQuery
  | TermOfUseQuery
  | PrivacyPolicyQuery
  | NotificationByTemplateQuery
  | InstitutionDetailsTabQuery

export type AppRouterQuery =
  | ConfettiQuery
  | SnackbarQuery
  | NavQuery
  | (ConfettiQuery & SnackbarQuery & NavQuery)

interface AppRouterHook {
  goToPage: (url: string, query?: AppRouterQuery, hideQuery?: boolean) => void
  goToPageCallback: (
    url: string,
    query?: AppRouterQuery,
    hideQuery?: boolean,
  ) => () => void
  router: NextRouter
  reload: () => void
}

interface MissionApplicationFailedQuery {
  missionApplicationFailed: WorkerIsNotEligibleForMissionError
  offerTab?: OfferTabs
}

const hasSnackBarQuery = (query: AppRouterQuery): query is SnackbarQuery =>
  'status' in query

const hasConfettiQuery = (query: AppRouterQuery): query is ConfettiQuery =>
  'applySuccessConfetti' in query

export const useAppRouter = (): AppRouterHook => {
  const router = useRouter()

  const goToPage = React.useCallback(
    (url: string, query?: AppRouterQuery, hideQuery?: boolean) => {
      let navQuery = query
      if (query && hasSnackBarQuery(query)) {
        navQuery = omit(query, ['message', 'color', 'status'])
      }
      if (query && hasConfettiQuery(query)) {
        navQuery = omit(query, ['applySuccessConfetti'])
      }

      /**
       * this is to prevent XSS attacks through the changing of the redirect param to include a javascript code
       */
      const isUnsafeUrl = url
        ? url.toLowerCase().indexOf('javascript') !== -1
        : false
      if (isUnsafeUrl) {
        return
      }

      // we should not use Next router for external urls
      if (url.startsWith('http')) {
        if (process.env['NODE_ENV'] !== 'production' && query) {
          throw new Error(
            'You should not use the query parameter of `goToPage` for external urls',
          )
        }
        windowLocation.assign(url)
        return
      }

      void router.push(
        {
          pathname: url,
          query: { ...(query ? query : {}) },
        },
        {
          pathname: url,
          query: { ...(navQuery && !hideQuery ? navQuery : {}) },
        },
      )
    },
    [router],
  )

  const goToPageCallback =
    (url: string, query?: AppRouterQuery, hideQuery = false) =>
    () =>
      goToPage(url, query, hideQuery)

  const reload = () => router.reload()

  return { goToPage, goToPageCallback, router, reload }
}
